import React from "react";

const BRAND_SHADOWS = {
    linkedin: "rgba(10, 102, 194, 0.22)",
    whatsapp: "rgba(39, 180, 62, 0.30)",
    meta: "rgba(0, 129, 251, 0.24)",
    facebook: "rgba(8, 102, 255, 0.24)",
    google: "rgba(251, 188, 4, 0.38)",
    googleads: "rgba(251, 188, 4, 0.38)",
    mail: "rgba(234, 67, 53, 0.24)",
    email: "rgba(234, 67, 53, 0.24)",
    gmail: "rgba(234, 67, 53, 0.24)",
    dialer: "rgba(32, 124, 151, 0.24)",
    phone: "rgba(32, 124, 151, 0.24)",
    teal: "rgba(32, 124, 151, 0.24)",
    twitter: "rgba(29, 155, 240, 0.24)",
    x: "rgba(0, 0, 0, 0.16)",
    instagram: "rgba(225, 48, 108, 0.26)",
    slack: "rgba(74, 21, 75, 0.24)",
};

const hexToRgba = (hex, alpha = 0.26) => {
    let cleaned = hex.replace("#", "").trim();
    if (cleaned.length === 3) {
        cleaned = cleaned.split("").map((c) => c + c).join("");
    }
    if (cleaned.length === 6) {
        const r = parseInt(cleaned.slice(0, 2), 16);
        const g = parseInt(cleaned.slice(2, 4), 16);
        const b = parseInt(cleaned.slice(4, 6), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return hex;
};

const detectColorFromChildren = (children) => {
    if (!children) return null;

    if (React.isValidElement(children)) {
        const { src, alt, color, fill, stroke, className } = children.props || {};

        if (color && typeof color === "string") return color;
        if (
            fill &&
            typeof fill === "string" &&
            !["none", "white", "#fff", "#ffffff"].includes(fill.toLowerCase())
        ) {
            return fill;
        }
        if (
            stroke &&
            typeof stroke === "string" &&
            !["none", "currentColor"].includes(stroke.toLowerCase())
        ) {
            return stroke;
        }

        const srcStr = String(src || "").toLowerCase();
        const altStr = String(alt || "").toLowerCase();
        const classStr = String(className || "").toLowerCase();
        const typeStr = String(
            children.type?.name || children.type?.displayName || ""
        ).toLowerCase();
        const combined = `${srcStr} ${altStr} ${classStr} ${typeStr}`;

        for (const [brand, brandColor] of Object.entries(BRAND_SHADOWS)) {
            if (combined.includes(brand)) {
                return brandColor;
            }
        }
    }

    return null;
};

const resolveShadowColor = (color, children) => {
    if (color) {
        const key = String(color).toLowerCase().trim();
        if (BRAND_SHADOWS[key]) return BRAND_SHADOWS[key];
        if (key.startsWith("#")) return hexToRgba(key, 0.26);
        return color;
    }

    const detected = detectColorFromChildren(children);
    if (detected) {
        if (detected.startsWith("#")) return hexToRgba(detected, 0.26);
        return detected;
    }

    return "rgba(10, 102, 194, 0.16)";
};

const SquircleIcon = ({
    variant = "default", // "default" | "secondary" | "white" | "card"
    children,
    className = "",
    bgColor = undefined,
    size = undefined,
    padding = undefined,
    shadow = undefined,
    shadowColor = undefined,
    channel = undefined,
    color = undefined,
    style = {},
    ...props
}) => {
    const isSecondary =
        variant === "secondary" ||
        variant === "white" ||
        variant === "card" ||
        variant === 2 ||
        variant === "2";

    if (isSecondary) {
        const resolvedSize = size || "w-[34px] h-[34px]";
        const resolvedBg = bgColor || "bg-white";
        const resolvedPadding = padding || "pt-[2px]";
        const chosenColor = shadowColor || channel || color;
        const outerShadowColor = resolveShadowColor(chosenColor, children);
        const dynamicBoxShadow =
            shadow ||
            `0 4px 4px 0 rgba(0, 0, 0, 0.03) inset, 0 6px 14px -2px ${outerShadowColor}`;

        return (
            <div
                className={`${resolvedSize} ${resolvedBg} ${resolvedPadding} rounded-[10px] border border-white flex items-center justify-center shrink-0 ${className}`}
                style={{
                    boxShadow: dynamicBoxShadow,
                    ...style,
                }}
                {...props}
            >
                {children}
            </div>
        );
    }

    const resolvedSize = size || "h-8 w-8";
    const resolvedBg = bgColor || "bg-[#207C9782]";
    const resolvedPadding = padding || "p-2";
    const resolvedShadow = shadow || "shadow-[0_4px_4px_0_rgba(255,255,255,0.28)_inset]";

    return (
        <>
            {/* Embedded Squircle clipPath definition */}
            <svg width="0" height="0" className="absolute pointer-events-none" aria-hidden="true">
                <defs>
                    <clipPath id="squircle-badge" clipPathUnits="objectBoundingBox">
                        <path d="M 0.5 0 C 0.7357 0, 0.8536 0, 0.9268 0.0732 C 1 0.1464, 1 0.2643, 1 0.5 C 1 0.7357, 1 0.8536, 0.9268 0.9268 C 0.8536 1, 0.7357 1, 0.5 1 C 0.2643 1, 0.1464 1, 0.0732 0.9268 C 0 0.8536, 0 0.7357, 0 0.5 C 0 0.2643, 0 0.1464, 0.0732 0.0732 C 0.1464 0, 0.2643 0, 0.5 0 Z" />
                    </clipPath>
                </defs>
            </svg>

            <div
                className={`${resolvedSize} ${resolvedBg} ${resolvedPadding} ${resolvedShadow} flex items-center justify-center flex-shrink-0 ${className}`}
                style={{
                    clipPath: "url(#squircle-badge)",
                    WebkitClipPath: "url(#squircle-badge)",
                    ...style,
                }}
                {...props}
            >
                {children}
            </div>
        </>
    );
};

export default SquircleIcon;
